import Link from 'next/link'
import styled from 'styled-components'

import { theme } from '@/styles/theme'

import { Line } from '@/components/elements/Line'
import { SpAccordion } from '@/components/elements/SpAccordion'
import { Spacer } from '@/components/elements/Spacer'

import type { PartialByKeys } from '@/types/util'

type Site = {
  name: string
  path: string
}

type Props = {
  parent: PartialByKeys<Site, 'path'>
  subdirectories: readonly Site[]
  isLast?: boolean
}

export const IrSiteMap = ({
  parent,
  subdirectories,
  isLast = false,
}: Props) => {
  if (isLast) {
    return (
      <SpAccordion title={parent.name}>
        <FullBox>
          <ParentName>{parent.name}</ParentName>
          <PcLine color="grey" weight={1} direction="horizon" />
          <SubdirectoryList direction="row">
            {subdirectories.map(({ name, path }) => (
              <li key={path}>
                <Link href={path} passHref>
                  <StyledLink>{name}</StyledLink>
                </Link>
              </li>
            ))}
          </SubdirectoryList>
        </FullBox>
      </SpAccordion>
    )
  }

  return (
    <SpAccordion title={parent.name}>
      <Box>
        <ParentName>
          {parent.path ? (
            <Link href={parent.path} passHref>
              <StyledLink>{parent.name}</StyledLink>
            </Link>
          ) : (
            parent.name
          )}
        </ParentName>
        <Spacer h={{ sp: 0, pc: 14 }} />
        <PcLine color="grey" weight={1} direction="horizon" />
        <Spacer h={{ sp: 0, pc: 30 }} />
        <SubdirectoryList direction="column">
          {subdirectories.map(({ name, path }) => (
            <li key={name}>
              <Link href={path} passHref>
                <StyledLink>{name}</StyledLink>
              </Link>
            </li>
          ))}
        </SubdirectoryList>
      </Box>
    </SpAccordion>
  )
}

const Box = styled.div`
  width: 316px;
  height: 100%;
  background: ${theme.colors.backgroundGrey};
  border-radius: 8px;
  padding: 26px 20px 32px;

  @media screen and (max-width: 1047px) {
    width: 100%;
    padding: 0;
  }
`

const FullBox = styled.div`
  width: 1040px;
  padding: 26px 20px 32px;
  background: ${theme.colors.backgroundGrey};
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media screen and (max-width: 1047px) {
    display: block;
    width: 100%;
    padding: 0;
  }
`

const PcLine = styled(Line)`
  @media screen and (max-width: 1047px) {
    display: none;
  }
`

const ParentName = styled.div`
  font-size: 20px;
  font-weight: 700;
  line-height: 120%;

  @media screen and (max-width: 1047px) {
    display: none;
  }
`

const SubdirectoryList = styled.ul<{ direction: 'row' | 'column' }>`
  display: flex;
  row-gap: 16px;
  column-gap: 20px;
  flex-direction: ${({ direction }) => direction};

  > li {
    font-size: 16px;
    line-height: 120%;
  }

  @media screen and (max-width: 1047px) {
    gap: 14px;
    flex-direction: column;

    > li {
      font-size: 14px;
    }
  }
`

const StyledLink = styled.a`
  color: ${theme.colors.textTitle};
  display: block;
  width: 100%;

  &:hover {
    text-decoration: underline;
    color: ${theme.colors.hoverLightBlue};
  }
`
